import React from 'react';
import type { FC } from 'react';
import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import Inline from '@atlaskit/primitives/inline';

import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import type { Snippet } from '../types/snippet.ts';
import { wrapAdfWithDocNode } from '../utils/adfUtils.ts';
import { useSnippetsActions, useSnippetsState } from '../store/snippetsStore.ts';
import {
	ANALYTICS_OPERATIONAL_SUBJECT,
	ANALYTICS_ORIGIN,
	ANALYTICS_UI_SUBJECT_ID,
	MODAL_OUTCOME,
	MODAL_TYPE,
} from '../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../hooks/analytics/useSnippetAnalytics.ts';
import { isBodyTooLargeError } from '../services/types/blockServiceTypes.ts';
import { useSnippetModalClose } from '../hooks/modals/useSnippetModalClose.ts';

import { getSnippetBaseModalProps, SnippetBaseModal } from './base/SnippetBaseModal.tsx';
import { SnippetShareButton } from './components/SnippetShareButton.tsx';
import type { SnippetBaseModalFormValues } from './utils/snippetModalFormUtils.ts';
import { getDefaultFormValues } from './utils/snippetModalFormUtils.ts';
import { editSnippetModalMessages } from './EditSnippetModal.i18n.ts';

export type EditSnippetModalProps = {
	snippet: Snippet;
	onClose: () => void;
	onCloseComplete: () => void;
	contentId: string;
	spaceId: string;
	spaceKey: string;
	editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
} & WithFlagsProps;

const EditSnippetModalWithFlags: FC<EditSnippetModalProps> = ({
	snippet,
	onClose,
	onCloseComplete,
	contentId,
	spaceId,
	spaceKey,
	flags,
	editorAPI,
}) => {
	const intl = useIntl();
	const { isSnippetSharingForSiteEnabled = false } = useSnippetsState();
	const snippetsActions = useSnippetsActions();
	const { fireFailedAnalytic, fireSuccessAnalytic } = useSnippetAnalytics(
		ANALYTICS_ORIGIN.EDIT_SNIPPET_MODAL,
	);
	const handleClose = useSnippetModalClose(
		onClose,
		ANALYTICS_ORIGIN.EDIT_SNIPPET_MODAL,
		ANALYTICS_UI_SUBJECT_ID.EDIT_SNIPPET,
	);

	const baseValues = getDefaultFormValues(isSnippetSharingForSiteEnabled);
	const defaultValues: SnippetBaseModalFormValues = {
		...baseValues,
		snippetName: snippet.name ?? '',
		snippetDescription: snippet.description ?? '',
		...(isSnippetSharingForSiteEnabled && { isShared: snippet.isShared ?? false }),
	};

	const initialAdf = wrapAdfWithDocNode(snippet?.body);
	const formKey = `edit-${snippet.snippetId}`;
	const title = intl.formatMessage(editSnippetModalMessages.titleEdit);
	const submitLabel = intl.formatMessage(editSnippetModalMessages.saveButton);

	const onSubmit = async (values: SnippetBaseModalFormValues, currentAdf: DocNode) => {
		const bodySize = JSON.stringify(currentAdf).length;
		const updatedSnippet = await snippetsActions.updateSnippet(
			snippet.snippetId,
			values.snippetName,
			currentAdf,
			values.snippetDescription,
			{
				flags,
				intl,
				editorAPI: editorAPI ?? undefined,
				isShared: values.isShared ?? false,
				previousIsShared: snippet.isShared ?? false,
				onShareFailure: (snippetId: string) =>
					fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_SHARE, { snippetId }),
				onUnshareFailure: (snippetId: string) =>
					fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_UNSHARE, {
						snippetId,
					}),
				onSaveFailure: (error: unknown) => {
					const isBodyTooLarge = isBodyTooLargeError(error);
					fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_UPDATE, {
						snippetId: snippet.snippetId,
						...(isBodyTooLarge && { isBodyTooLarge: true, bodySize }),
					});
				},
			},
		);
		if (updatedSnippet) {
			fireSuccessAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_API_SAVE_SUCCESS, {
				snippetId: updatedSnippet.snippetId,
				bodySize,
				modalType: MODAL_TYPE.EDIT,
			});
			handleClose({
				outcome: MODAL_OUTCOME.EDITED,
				snippetId: updatedSnippet.snippetId,
			});
		}
	};

	return (
		<SnippetBaseModal
			{...getSnippetBaseModalProps({
				title,
				submitLabel,
				formKey,
				defaultValues,
				initialAdf,
				onSubmit,
				handleClose,
				fireFailedAnalytic,
				onCloseComplete,
				contentId,
				spaceId,
				spaceKey,
				snippetId: snippet.snippetId,
				snippetScope: snippet.scope,
				isSnippetSharingForSiteEnabled,
				extraActions: snippet.shareUrl ? (
					<Inline alignInline="end">
						<SnippetShareButton shareUrl={snippet.shareUrl} />
					</Inline>
				) : undefined,
			})}
		/>
	);
};

export const EditSnippetModal = withFlags(EditSnippetModalWithFlags);
